import React from 'react';
import { Modal, Table, Typography, Spin, Alert, Empty, Tag, Button, Space, Input, Tooltip, Badge } from 'antd';
import { SearchOutlined, CopyOutlined, FileExcelOutlined, FileTextOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';
import { useMonitoredAddresses } from '../../hooks/useIndexerManagement';
import { formatDate } from '../../utils/formatters';

const { Text } = Typography;

interface MonitoredAddressesModalProps {
  blockchain: string;
  visible: boolean;
  onClose: () => void;
}

/**
 * Modal component to display monitored addresses for a specific blockchain
 * Addresses are sorted by creation date with newest wallets at the top and older ones at the bottom
 */
const MonitoredAddressesModal: React.FC<MonitoredAddressesModalProps> = ({
  blockchain,
  visible,
  onClose
}) => {
  const { t: indexerT } = useTranslationWithNamespace('indexer');
  const [searchText, setSearchText] = React.useState('');

  // Fetch monitored addresses when the modal is visible
  const { data, isLoading, isError, error } = useMonitoredAddresses(blockchain, visible);

  // Process and normalize address data
  const processedAddresses = React.useMemo(() => {
    if (!data?.addresses || !Array.isArray(data.addresses)) return [];

    // Map addresses and ensure all required fields are present
    const addresses = data.addresses.map((addr: any) => {
      // Handle both string addresses and object addresses
      if (typeof addr === 'string') {
        return {
          address: addr,
          // Add a default creation date (current time) if not present
          createdAt: new Date().toISOString()
        };
      } else if (typeof addr === 'object' && addr !== null) {
        // Ensure the address object has an address property
        return {
          ...addr,
          address: addr.address || JSON.stringify(addr),
          // Use existing createdAt field which should now be provided by the API
          // Fall back to other possible field names or current time as last resort
          createdAt: addr.createdAt || addr.addedAt || addr.create_at || new Date().toISOString()
        };
      }
      return {
        address: String(addr),
        createdAt: new Date().toISOString()
      };
    });

    // Sort addresses by creation date (newest first)
    return addresses.sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
    });
  }, [data]);

  // Filter addresses based on search text
  const filteredAddresses = React.useMemo(() => {
    if (!processedAddresses.length) return [];

    // If no search text, return the already sorted processedAddresses
    if (!searchText) return processedAddresses;

    // Filter addresses and maintain the sort order (they're already sorted in processedAddresses)
    return processedAddresses.filter((item: any) =>
      (item.address || '').toLowerCase().includes(searchText.toLowerCase())
    );
  }, [processedAddresses, searchText]);

  // Copy address to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You can add a notification here if you want
  };

  // Export addresses to CSV
  const exportToCSV = () => {
    if (!processedAddresses.length) return;

    // Create CSV content
    const headers = ['Address', 'User ID', 'Status', 'Created At'];
    const csvContent = [
      headers.join(','),
      ...processedAddresses.map((addr: any) => {
        const createdAt = addr.createdAt || addr.addedAt || addr.create_at || '';
        return `"${addr.address}","${addr.userId || ''}","active","${createdAt}"`;
      })
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${blockchain}-addresses-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export addresses to JSON
  const exportToJSON = () => {
    if (!processedAddresses.length) return;

    // Create JSON content
    const jsonContent = JSON.stringify(processedAddresses, null, 2);

    // Create and download the file
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${blockchain}-addresses-${new Date().toISOString().split('T')[0]}.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Table columns
  const columns = [
    {
      title: indexerT('address'),
      dataIndex: 'address',
      key: 'address',
      render: (text: string) => (
        <Space>
          <Text copyable={{ text, tooltips: [indexerT('copy'), indexerT('copied')] }}>
            {text}
          </Text>
          <Button
            icon={<CopyOutlined />}
            size="small"
            type="text"
            onClick={() => copyToClipboard(text)}
          />
        </Space>
      )
    },
    {
      title: indexerT('userId'),
      dataIndex: 'userId',
      key: 'userId',
      render: (userId: any) => {
        if (userId === undefined || userId === null) {
          return '-';
        }
        return String(userId);
      }
    },
    {
      title: indexerT('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        // For monitored addresses, we assume they are active by default
        // since they are being tracked by the indexer
        const addressStatus = status || 'active';

        switch (addressStatus.toLowerCase()) {
          case 'active':
            return <Badge status="success" text={indexerT('active')} />;
          case 'inactive':
            return <Badge status="error" text={indexerT('inactive')} />;
          case 'pending':
            return <Badge status="processing" text={indexerT('pending')} />;
          default:
            return <Badge status="default" text={indexerT('unknown')} />;
        }
      }
    },
    {
      title: indexerT('createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string, record: any) => {
        // Try to use createdAt, addedAt, or create_at in that order
        const displayDate = date || record.addedAt || record.create_at;
        if (!displayDate) return '-';
        return formatDate(displayDate);
      },
      sorter: (a: any, b: any) => {
        const dateA = new Date(a.createdAt || a.addedAt || a.create_at || 0);
        const dateB = new Date(b.createdAt || b.addedAt || b.create_at || 0);
        return dateB.getTime() - dateA.getTime(); // Default sort is newest first
      },
      defaultSortOrder: 'descend' as const
    }
  ];

  // Prepare data for the table and ensure it's sorted by creation date (newest first)
  const tableData = filteredAddresses
    .map((item: any, index: number) => ({
      key: index,
      ...item
    }))
    // Sort by creation date (newest first)
    .sort((a, b) => {
      const dateA = new Date(a.createdAt || a.addedAt || a.create_at || 0).getTime();
      const dateB = new Date(b.createdAt || b.addedAt || b.create_at || 0).getTime();
      return dateB - dateA; // Descending order (newest first)
    });

  return (
    <Modal
      title={`${indexerT('monitoredAddresses')} - ${blockchain.toUpperCase()}`}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          {indexerT('close')}
        </Button>
      ]}
      width={800}
    >
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Input
          placeholder={indexerT('searchAddresses')}
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <Space>
          <Tooltip title={indexerT('exportToCSV')}>
            <Button
              icon={<FileExcelOutlined />}
              onClick={exportToCSV}
              disabled={!processedAddresses.length}
            >
              {indexerT('exportCSV')}
            </Button>
          </Tooltip>
          <Tooltip title={indexerT('exportToJSON')}>
            <Button
              icon={<FileTextOutlined />}
              onClick={exportToJSON}
              disabled={!processedAddresses.length}
            >
              {indexerT('exportJSON')}
            </Button>
          </Tooltip>
        </Space>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
        </div>
      ) : isError ? (
        <Alert
          message={indexerT('errorMessages.title')}
          description={String(error)}
          type="error"
          showIcon
        />
      ) : !filteredAddresses.length ? (
        <Empty description={indexerT('noAddressesFound')} />
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <Text>
              {indexerT('totalAddresses')}: <Tag color="blue">{filteredAddresses.length}</Tag>
            </Text>
          </div>
          <Table
            columns={columns}
            dataSource={tableData}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) => `${range[0]}-${range[1]} ${indexerT('of')} ${total} ${indexerT('items')}`
            }}
            size="middle"
            showSorterTooltip={true}
          // The table is already sorted by creation date in the tableData preparation
          />
        </>
      )}
    </Modal>
  );
};

export default MonitoredAddressesModal;
